import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiService } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import UniversalModal from '../Common/UniversalModal';
import Toast from '../Common/Toast';
import '../Common/CardActions.css';
import Pagination from '../Common/Pagination';

const emptyForm = { name: '', inn: '', site: '', operator: '', fogsoft_host: '' };
const ETP_COL_WIDTH_DEFAULTS = {
  name: 280,
  inn: 140,
  site: 340,
  operator: 340,
  actions: 140,
};
const ETP_COL_WIDTH_LIMITS = { min: 80, max: 900 };
const ETPS_TABLE_WIDTHS_CACHE_KEY = 'aot_etps_table_col_widths_v1';

const normalizeEtpColWidths = (raw) => {
  const next = { ...ETP_COL_WIDTH_DEFAULTS };
  if (!raw || typeof raw !== 'object') return next;
  Object.keys(ETP_COL_WIDTH_DEFAULTS).forEach((key) => {
    const n = Number(raw[key]);
    if (!Number.isFinite(n)) return;
    next[key] = Math.max(ETP_COL_WIDTH_LIMITS.min, Math.min(ETP_COL_WIDTH_LIMITS.max, Math.round(n)));
  });
  return next;
};

const readCachedEtpColWidths = () => {
  try {
    const raw = localStorage.getItem(ETPS_TABLE_WIDTHS_CACHE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (_) {
    return {};
  }
};

const isAdminUser = (user) => {
  return !!user?.is_staff || !!user?.is_superuser || String(user?.role || '').toLowerCase() === 'admin';
};

const formatStatus = (s) => {
  if (s === 'running') return 'Выполняется';
  if (s === 'pending') return 'В очереди';
  if (s === 'done') return 'Завершено';
  if (s === 'failed') return 'Ошибка';
  return s || '—';
};

const EtpsList = () => {
  const { user } = useAuth();
  const isAdmin = isAdminUser(user);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [toast, setToast] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(24);
  const [etpColWidths, setEtpColWidths] = useState(readCachedEtpColWidths);

  const [modalState, setModalState] = useState({ isOpen: false, mode: null, item: null }); // create|view|edit
  const [formData, setFormData] = useState(emptyForm);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const [importJob, setImportJob] = useState(null);
  const importJobRef = useRef(null);
  const importJobStatusRef = useRef(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importStarting, setImportStarting] = useState(false);
  const [operatorJob, setOperatorJob] = useState(null);
  const operatorJobRef = useRef(null);
  const operatorJobStatusRef = useRef(null);
  const [operatorLoading, setOperatorLoading] = useState(false);
  const [operatorStarting, setOperatorStarting] = useState(false);
  const importFileRef = useRef(null);
  const etpColWidthsRef = useRef(etpColWidths);
  const resizeStateRef = useRef(null);
  const resizeRafRef = useRef(null);

  useEffect(() => {
    etpColWidthsRef.current = etpColWidths;
  }, [etpColWidths]);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 2500);
  };

  const normalizeSiteUrl = (raw) => {
    const s = String(raw || '').trim();
    if (!s) return '';
    if (/^[a-z][a-z0-9+.-]*:\/\//i.test(s)) return s;
    return `https://${s}`;
  };

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const all = [];
      let page = 1;
      while (true) {
        const resp = await apiService.getEtps(`?page=${page}`);
        const chunk = resp.data.results || [];
        all.push(...chunk);
        if (!resp.data.next) break;
        page += 1;
        if (page > 500) break;
      }
      setItems(all);
    } catch (err) {
      setError('Ошибка при загрузке ЭТП: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadLatestImportJob = useCallback(async () => {
    if (!isAdmin) return;
    setImportLoading(true);
    try {
      const resp = await apiService.listEtpImportJobs('?page=1&job_type=import');
      const first = (resp.data?.results || [])[0] || null;
      setImportJob(first);
    } catch (err) {
      console.error('Не удалось загрузить статус импорта ЭТП', err);
    } finally {
      setImportLoading(false);
    }
  }, [isAdmin]);

  const loadLatestOperatorJob = useCallback(async () => {
    if (!isAdmin) return;
    setOperatorLoading(true);
    try {
      const resp = await apiService.listEtpImportJobs('?page=1&job_type=operator_update');
      const first = (resp.data?.results || [])[0] || null;
      setOperatorJob(first);
    } catch (err) {
      console.error('Не удалось загрузить статус обновления операторов', err);
    } finally {
      setOperatorLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (isAdmin) loadLatestImportJob(); }, [isAdmin, loadLatestImportJob]);
  useEffect(() => { if (isAdmin) loadLatestOperatorJob(); }, [isAdmin, loadLatestOperatorJob]);

  useEffect(() => { importJobRef.current = importJob; }, [importJob]);
  useEffect(() => { operatorJobRef.current = operatorJob; }, [operatorJob]);

  useEffect(() => {
    (async () => {
      try {
        const resp = await apiService.getUiSettings();
        const v = Number(resp?.data?.table_page_size);
        if ([12, 24, 48, 96].includes(v)) {
          setItemsPerPage(v);
        }
        if (isAdmin) {
          const widths = resp?.data?.etps_table_column_widths;
          if (widths && typeof widths === 'object') {
            setEtpColWidths(widths);
            localStorage.setItem(ETPS_TABLE_WIDTHS_CACHE_KEY, JSON.stringify(widths));
          }
        }
      } catch (_) {
        // ignore
      }
    })();
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin) return;
    const prev = importJobStatusRef.current;
    const next = importJob?.status || null;
    importJobStatusRef.current = next;
    if ((prev === 'pending' || prev === 'running') && next === 'done') {
      load();
    }
  }, [importJob?.status, isAdmin, load]);

  useEffect(() => {
    if (!isAdmin) return;
    const prev = operatorJobStatusRef.current;
    const next = operatorJob?.status || null;
    operatorJobStatusRef.current = next;
    if ((prev === 'pending' || prev === 'running') && next === 'done') {
      load();
    }
  }, [operatorJob?.status, isAdmin, load]);

  useEffect(() => {
    if (!isAdmin) return undefined;
    const timer = setInterval(async () => {
      const job = importJobRef.current;
      if (!job?.id) return;
      if (job.status !== 'pending' && job.status !== 'running') return;
      try {
        const resp = await apiService.getEtpImportJob(job.id);
        setImportJob(resp.data);
      } catch (err) {
        // ignore
      }
    }, 1200);
    return () => clearInterval(timer);
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin) return undefined;
    const timer = setInterval(async () => {
      const job = operatorJobRef.current;
      if (!job?.id) return;
      if (job.status !== 'pending' && job.status !== 'running') return;
      try {
        const resp = await apiService.getEtpImportJob(job.id);
        setOperatorJob(resp.data);
      } catch (err) {
        // ignore
      }
    }, 1200);
    return () => clearInterval(timer);
  }, [isAdmin]);

  const filtered = useMemo(() => {
    const q = String(search || '').trim().toLowerCase();
    if (!q) return items;
    return (items || []).filter((x) => {
      const hay = `${x.name || ''} ${x.inn || ''} ${x.site || ''} ${x.operator || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [items, search]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, itemsPerPage, items]);

  const totalItems = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const paginated = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filtered.slice(start, start + itemsPerPage);
  }, [filtered, currentPage, itemsPerPage]);

  const handleItemsPerPageChange = (v) => {
    setItemsPerPage(v);
    setCurrentPage(1);
    apiService.saveUiSettings({ table_page_size: v }).catch(() => {});
  };

  const effectiveEtpColWidths = useMemo(() => normalizeEtpColWidths(etpColWidths), [etpColWidths]);

  const persistEtpColWidthsCache = useCallback((next) => {
    try {
      localStorage.setItem(ETPS_TABLE_WIDTHS_CACHE_KEY, JSON.stringify(next || {}));
    } catch (_) {}
  }, []);

  const startResizeColumn = useCallback((e, key) => {
    if (!isAdmin) return;
    e.preventDefault();
    e.stopPropagation();
    resizeStateRef.current = {
      key,
      startX: e.clientX,
      startWidth: Number(effectiveEtpColWidths[key] || 100),
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [effectiveEtpColWidths, isAdmin]);

  useEffect(() => {
    const onMove = (e) => {
      const state = resizeStateRef.current;
      if (!state) return;
      const delta = e.clientX - state.startX;
      const nextWidth = Math.max(ETP_COL_WIDTH_LIMITS.min, Math.min(ETP_COL_WIDTH_LIMITS.max, state.startWidth + delta));
      if (resizeRafRef.current) cancelAnimationFrame(resizeRafRef.current);
      resizeRafRef.current = requestAnimationFrame(() => {
        setEtpColWidths((prev) => {
          const next = { ...(prev || {}), [state.key]: Math.round(nextWidth) };
          persistEtpColWidthsCache(next);
          return next;
        });
      });
    };
    const onUp = () => {
      if (!resizeStateRef.current) return;
      resizeStateRef.current = null;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      const normalized = normalizeEtpColWidths(etpColWidthsRef.current || {});
      setEtpColWidths(normalized);
      persistEtpColWidthsCache(normalized);
      if (isAdmin) {
        apiService.saveUiSettings({ etps_table_column_widths: normalized }).catch(() => {
          setError('Не удалось сохранить ширины колонок ЭТП');
        });
      }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [isAdmin, persistEtpColWidthsCache]);

  const openCreate = () => {
    setFormData(emptyForm);
    setHasUnsavedChanges(false);
    setModalState({ isOpen: true, mode: 'create', item: null });
  };

  const openView = (item) => {
    setFormData({
      name: item?.name || '',
      inn: item?.inn || '',
      site: item?.site || '',
      operator: item?.operator || '',
      fogsoft_host: item?.fogsoft_host || '',
    });
    setHasUnsavedChanges(false);
    setModalState({ isOpen: true, mode: 'view', item });
  };

  const openEdit = (item) => {
    if (!isAdmin) return;
    setFormData({
      name: item?.name || '',
      inn: item?.inn || '',
      site: item?.site || '',
      operator: item?.operator || '',
      fogsoft_host: item?.fogsoft_host || '',
    });
    setHasUnsavedChanges(false);
    setModalState({ isOpen: true, mode: 'edit', item });
  };

  const closeModal = () => {
    setModalState({ isOpen: false, mode: null, item: null });
    setFormData(emptyForm);
    setHasUnsavedChanges(false);
  };

  const onField = (key, value) => {
    if (key === 'inn') {
      const digits = String(value || '').replace(/\D/g, '').slice(0, 12);
      setFormData((prev) => ({ ...prev, [key]: digits }));
      setHasUnsavedChanges(true);
      return;
    }
    setFormData((prev) => ({ ...prev, [key]: value }));
    setHasUnsavedChanges(true);
  };

  const handleSave = async ({ exit } = {}) => {
    if (!isAdmin) return;
    try {
      let saved = null;
      if (modalState.item?.id) {
        const resp = await apiService.updateEtp(modalState.item.id, formData);
        saved = resp.data;
        setItems((prev) => (prev || []).map((x) => (x.id === saved.id ? saved : x)));
        showToast('Изменения сохранены');
      } else {
        const resp = await apiService.createEtp(formData);
        saved = resp.data;
        setItems((prev) => [saved, ...(prev || [])]);
        showToast('ЭТП добавлена');
      }
      setHasUnsavedChanges(false);
      if (exit) closeModal();
      else openEdit(saved);
    } catch (err) {
      const msg = err.response?.data?.detail || err.response?.data?.error || err.message;
      setError('Ошибка при сохранении ЭТП: ' + msg);
      showToast('Ошибка при сохранении ЭТП', 'error');
    }
  };

  const handleDelete = async (item) => {
    if (!isAdmin) return;
    if (!window.confirm(`Удалить ЭТП ${item?.name || ''}?`)) return;
    try {
      await apiService.deleteEtp(item.id);
      setItems((prev) => (prev || []).filter((x) => x.id !== item.id));
      showToast('ЭТП удалена');
    } catch (err) {
      const msg = err.response?.data?.detail || err.response?.data?.error || err.message;
      setError('Ошибка при удалении ЭТП: ' + msg);
      showToast('Ошибка при удалении ЭТП', 'error');
    }
  };

  const startImport = async () => {
    if (!isAdmin) return;
    setImportStarting(true);
    try {
      const resp = await apiService.startEtpImport();
      setImportJob(resp.data);
      showToast('Импорт ЭТП запущен');
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      showToast('Не удалось запустить импорт ЭТП: ' + msg, 'error');
    } finally {
      setImportStarting(false);
    }
  };

  const startOperatorUpdate = async () => {
    if (!isAdmin) return;
    setOperatorStarting(true);
    try {
      const resp = await apiService.startEtpOperatorUpdate();
      setOperatorJob(resp.data);
      showToast('Обновление операторов запущено');
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      showToast('Не удалось запустить обновление операторов: ' + msg, 'error');
    } finally {
      setOperatorStarting(false);
    }
  };

  const refreshImportStatus = async () => {
    if (!isAdmin) return;
    if (!importJob?.id) return;
    try {
      const resp = await apiService.getEtpImportJob(importJob.id);
      setImportJob(resp.data);
    } catch (err) {
      // ignore
    }
  };

  const refreshOperatorStatus = async () => {
    if (!isAdmin) return;
    if (!operatorJob?.id) return;
    try {
      const resp = await apiService.getEtpImportJob(operatorJob.id);
      setOperatorJob(resp.data);
    } catch (err) {
      // ignore
    }
  };

  const handleExport = async () => {
    if (!isAdmin) return;
    try {
      const resp = await apiService.exportEtps();
      const blob = new Blob([resp.data], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'etps_export.json';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      showToast('Экспорт выполнен');
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      showToast('Ошибка экспорта: ' + msg, 'error');
    }
  };

  const handleImportClick = () => {
    if (!isAdmin) return;
    importFileRef.current?.click();
  };

  const handleImportFile = async (e) => {
    if (!isAdmin) return;
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const formDataFile = new FormData();
      formDataFile.append('file', file);
      const resp = await apiService.importEtps(formDataFile);
      const { created = 0, updated = 0, skipped = 0 } = resp.data || {};
      showToast(`Импорт выполнен: создано ${created}, обновлено ${updated}, пропущено ${skipped}`);
      load();
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.detail || err.message;
      showToast('Ошибка импорта: ' + msg, 'error');
    } finally {
      e.target.value = '';
    }
  };

  if (loading) {
    return <div style={{ padding: 50, textAlign: 'center' }}><h2>Загрузка ЭТП...</h2></div>;
  }

  const progressPct = Math.max(
    0,
    Math.min(100, (importJob?.total_pages ? (importJob.current_page / importJob.total_pages) * 100 : 0)),
  );

  return (
    <div style={{ padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h1>🏷️ ЭТП</h1>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {isAdmin && (
            <input
              ref={importFileRef}
              type="file"
              accept="application/json"
              style={{ display: 'none' }}
              onChange={handleImportFile}
            />
          )}
          <button
            type="button"
            onClick={load}
            style={{ padding: '10px 16px', background: '#f8f9fa', color: '#2c3e50', border: '1px solid #dee2e6', borderRadius: 8, cursor: 'pointer' }}
          >
            Обновить
          </button>
          {isAdmin && (
            <>
              <button
                type="button"
                onClick={handleExport}
                style={{ padding: '10px 16px', background: '#6c757d', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer' }}
              >
                Экспорт
              </button>
              <button
                type="button"
                onClick={handleImportClick}
                style={{ padding: '10px 16px', background: '#6c757d', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer' }}
              >
                Импорт
              </button>
              <button
                type="button"
                onClick={startImport}
                disabled={importStarting || importJob?.status === 'running' || importJob?.status === 'pending'}
                style={{ padding: '10px 16px', background: '#0d6efd', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer' }}
                title="Импорт с Федресурса (old.bankrot.fedresurs.ru)"
              >
                {importStarting ? 'Запуск…' : 'Импорт с Федресурса'}
              </button>
              <button
                type="button"
                onClick={startOperatorUpdate}
                disabled={operatorStarting || operatorJob?.status === 'running' || operatorJob?.status === 'pending'}
                style={{ padding: '10px 16px', background: '#20c997', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer' }}
                title="Обновить оператора ЭТП по данным ЕГРЮЛ (egrul.itsoft.ru)"
              >
                {operatorStarting ? 'Запуск…' : 'Обновить операторов'}
              </button>
              <button
                type="button"
                onClick={openCreate}
                style={{ padding: '10px 16px', background: '#007bff', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer' }}
              >
                Добавить
              </button>
            </>
          )}
        </div>
      </div>

      {isAdmin && (
        <>
          <div style={{ background: '#fff', border: '1px solid #e9ecef', borderRadius: 12, padding: 14, marginBottom: 12 }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ color: '#6c757d' }}>
                Статус импорта: <b>{formatStatus(importJob?.status)}</b>
                {importLoading && <span> · загрузка…</span>}
              </div>
              <button type="button" onClick={refreshImportStatus} disabled={!importJob?.id} style={{ padding: '8px 12px', border: '1px solid #dee2e6', borderRadius: 8, background: '#f8f9fa' }}>
                Обновить статус
              </button>
              <div style={{ color: '#6c757d' }}>
                {importJob
                  ? `стр. ${importJob.current_page || 0}/${importJob.total_pages || 0} · обработано ${importJob.processed || 0} · создано ${importJob.created || 0} · обновлено ${importJob.updated || 0} · пропущено ${importJob.skipped || 0}`
                  : '—'}
              </div>
            </div>
            <div style={{ marginTop: 10, height: 10, background: '#e9ecef', borderRadius: 999, overflow: 'hidden' }}>
              <div style={{ width: `${progressPct}%`, height: '100%', background: importJob?.status === 'failed' ? '#dc3545' : '#0d6efd', transition: 'width 0.2s ease' }} />
            </div>
            {!!importJob?.message && (
              <div style={{ marginTop: 10, color: '#495057', whiteSpace: 'pre-wrap' }}>
                {String(importJob.message).trim()}
              </div>
            )}
          </div>
          <div style={{ background: '#fff', border: '1px solid #e9ecef', borderRadius: 12, padding: 14, marginBottom: 16 }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ color: '#6c757d' }}>
                Обновление операторов: <b>{formatStatus(operatorJob?.status)}</b>
                {operatorLoading && <span> · загрузка…</span>}
              </div>
              <button type="button" onClick={refreshOperatorStatus} disabled={!operatorJob?.id} style={{ padding: '8px 12px', border: '1px solid #dee2e6', borderRadius: 8, background: '#f8f9fa' }}>
                Обновить статус
              </button>
              <div style={{ color: '#6c757d' }}>
                {operatorJob
                  ? `обработано ${operatorJob.processed || 0} · обновлено ${operatorJob.updated || 0} · пропущено ${operatorJob.skipped || 0}`
                  : '—'}
              </div>
            </div>
            {!!operatorJob?.message && (
              <div style={{ marginTop: 10, color: '#495057', whiteSpace: 'pre-wrap' }}>
                {String(operatorJob.message).trim()}
              </div>
            )}
          </div>
        </>
      )}

      <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center' }}>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Поиск по ЭТП, ИНН, сайту, оператору..."
          style={{ flex: 1, minWidth: 260, padding: 10, border: '1px solid #dee2e6', borderRadius: 10 }}
        />
        <div style={{ color: '#6c757d', whiteSpace: 'nowrap' }}>
          Найдено: <b>{filtered.length}</b>
        </div>
      </div>

      {error && (
        <div style={{ background: '#fee', border: '1px solid #fcc', color: '#a00', padding: 12, borderRadius: 8, marginBottom: 16 }}>
          {error}
        </div>
      )}

      <div style={{ background: '#fff', border: '1px solid #e9ecef', borderRadius: 12, overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1100 }}>
          <colgroup>
            <col style={{ width: effectiveEtpColWidths.name }} />
            <col style={{ width: effectiveEtpColWidths.inn }} />
            <col style={{ width: effectiveEtpColWidths.site }} />
            <col style={{ width: effectiveEtpColWidths.operator }} />
            {isAdmin && <col style={{ width: effectiveEtpColWidths.actions }} />}
          </colgroup>
          <thead>
            <tr style={{ background: '#f8f9fa' }}>
              {[
                { key: 'name', label: 'Наименование' },
                { key: 'inn', label: 'ИНН' },
                { key: 'site', label: 'Сайт' },
                { key: 'operator', label: 'Оператор ЭТП' },
              ].map((col) => (
                <th key={col.key} style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #e9ecef', position: 'relative' }}>
                  <span>{col.label}</span>
                  {!!isAdmin && (
                    <span
                      role="presentation"
                      onMouseDown={(e) => startResizeColumn(e, col.key)}
                      onClick={(e) => e.stopPropagation()}
                      title="Изменить ширину столбца"
                      style={{ position: 'absolute', top: 0, right: -2, width: 10, height: '100%', cursor: 'col-resize' }}
                    />
                  )}
                </th>
              ))}
              {isAdmin && (
                <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #e9ecef', position: 'relative' }}>
                  <span>Действия</span>
                  <span
                    role="presentation"
                    onMouseDown={(e) => startResizeColumn(e, 'actions')}
                    onClick={(e) => e.stopPropagation()}
                    title="Изменить ширину столбца"
                    style={{ position: 'absolute', top: 0, right: -2, width: 10, height: '100%', cursor: 'col-resize' }}
                  />
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={isAdmin ? 5 : 4} style={{ padding: 20, textAlign: 'center', color: '#6c757d', fontSize: '13px' }}>
                  ЭТП не найдены
                </td>
              </tr>
            ) : paginated.map((x) => (
              <tr key={x.id} onClick={() => openView(x)} style={{ cursor: 'pointer' }}>
                <td style={{ padding: '10px 12px', borderBottom: '1px solid #f1f3f5', fontSize: '13px' }}>{x.name}</td>
                <td style={{ padding: '10px 12px', borderBottom: '1px solid #f1f3f5', fontSize: '13px' }}>{x.inn || '—'}</td>
                <td style={{ padding: '10px 12px', borderBottom: '1px solid #f1f3f5', fontSize: '13px' }}>
                  {x.site ? (
                    <a
                      href={normalizeSiteUrl(x.site)}
                      onClick={(e) => e.stopPropagation()}
                      target="_blank"
                      rel="noreferrer"
                      style={{ color: '#0b6aa2', textDecorationColor: '#0b6aa2', fontWeight: 500 }}
                    >
                      {x.site}
                    </a>
                  ) : '—'}
                </td>
                <td style={{ padding: '10px 12px', borderBottom: '1px solid #f1f3f5', fontSize: '13px' }}>{x.operator || '—'}</td>
                {isAdmin && (
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid #f1f3f5', fontSize: '13px' }}>
                    <div className="card-actions" style={{ justifyContent: 'flex-start' }}>
                      <button
                        type="button"
                        className="card-action-btn card-action-btn--edit"
                        title="Редактировать"
                        onClick={(e) => { e.stopPropagation(); openEdit(x); }}
                      >
                        ✎
                      </button>
                      <button
                        type="button"
                        className="card-action-btn card-action-btn--delete"
                        title="Удалить"
                        onClick={(e) => { e.stopPropagation(); handleDelete(x); }}
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        itemsPerPage={itemsPerPage}
        totalItems={totalItems}
        onItemsPerPageChange={handleItemsPerPageChange}
      />

      {modalState.isOpen && (
        <UniversalModal
          isOpen={modalState.isOpen}
          onClose={closeModal}
          title={
            modalState.mode === 'create' ? 'Добавить ЭТП'
              : modalState.mode === 'edit' ? `ЭТП: ${modalState.item?.name || ''}`
                : `ЭТП: ${modalState.item?.name || ''}`
          }
          onSave={modalState.mode === 'view' ? null : () => handleSave({ exit: false })}
          onSaveAndExit={modalState.mode === 'view' ? null : () => handleSave({ exit: true })}
          onCancel={closeModal}
          hasChanges={hasUnsavedChanges}
          isViewMode={modalState.mode === 'view'}
          onEdit={modalState.mode === 'view' && isAdmin ? () => openEdit(modalState.item) : null}
        >
          <div style={{ padding: 8 }}>
            <div className="form-group">
              <label>Наименование:</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => onField('name', e.target.value)}
                placeholder="Наименование ЭТП"
                disabled={modalState.mode === 'view'}
              />
            </div>
            <div className="form-group">
              <label>ИНН:</label>
              <input
                type="text"
                value={formData.inn}
                onChange={(e) => onField('inn', e.target.value)}
                placeholder="1234567890"
                disabled={modalState.mode === 'view'}
              />
            </div>
            <div className="form-group">
              <label>Сайт:</label>
              <input
                type="text"
                value={formData.site}
                onChange={(e) => onField('site', e.target.value)}
                placeholder="https://..."
                disabled={modalState.mode === 'view'}
              />
            </div>
            <div className="form-group">
              <label>Оператор ЭТП:</label>
              <input
                type="text"
                value={formData.operator}
                onChange={(e) => onField('operator', e.target.value)}
                placeholder="Оператор"
                disabled={modalState.mode === 'view'}
              />
            </div>
            <div className="form-group">
              <label>Хост FogSoft:</label>
              <input
                type="text"
                value={formData.fogsoft_host}
                onChange={(e) => onField('fogsoft_host', e.target.value)}
                placeholder="например: bankruptcy.bepspb.ru"
                disabled={modalState.mode === 'view'}
              />
            </div>
          </div>
        </UniversalModal>
      )}

      <Toast toast={toast} />
    </div>
  );
};

export default EtpsList;
